/**
 * StatusEffectManager — Client-side tracker for status effect stacking.
 *
 * L3.4 — this is now a PRESENTATION-ONLY layer. Tick damage is owned
 * by the server (`StatusEffectService`); the client tracks stack counts
 * for the effect-icon UI + emits VFX events. The pre-L3.4 setInterval
 * that emitted `combat:status_tick` with a damage number has been
 * removed — it created duplicate FCTs once the server started
 * broadcasting `playerDamaged` per tick.
 *
 * Plan: docs/plans (L3.4)
 */

import { EventBus } from '../../../shared/EventBus';

const eventBus = EventBus.getInstance();

export interface StatusEffect {
  effectId: string;
  stacks: number;
  maxStacks: number;
  duration: number;
  expiresAt: number;
  tickDamage: number;
  tickRateMs: number;
  sourceId: string;
  data?: Record<string, unknown> | null;
}

export interface ApplyStatusConfig {
  effectId: string;
  maxStacks?: number;
  duration: number;
  tickDamage?: number;
  tickRateMs?: number;
  sourceId: string;
  targetId: string;
  data?: Record<string, unknown> | null;
  /** False for an authoritative echo that confirms/refreshes local prediction. */
  stack?: boolean;
}

class StatusEffectManagerImpl {
  private effects = new Map<string, Map<string, StatusEffect>>();

  /** Apply or stack a status effect on a target.
   *  L3.4 — no longer schedules a self-tick. Duration tracking + VFX
   *  emission only; server is sole authority for HP changes. */
  apply(config: ApplyStatusConfig): void {
    const {
      targetId,
      effectId,
      maxStacks = 1,
      duration,
      tickDamage = 0,
      tickRateMs = 1000,
      sourceId,
      data,
      stack = true,
    } = config;

    if (!this.effects.has(targetId)) {
      this.effects.set(targetId, new Map());
    }
    const targetEffects = this.effects.get(targetId)!;

    if (targetEffects.has(effectId)) {
      // Locally authored repeated applications can stack to their explicit
      // cap. Authoritative echoes pass stack:false so prediction plus wire
      // confirmation refreshes the timer without inventing another stack.
      const existing = targetEffects.get(effectId)!;
      if (stack) {
        existing.stacks = Math.min(existing.stacks + 1, maxStacks);
      }
      existing.maxStacks = maxStacks;
      existing.expiresAt = Date.now() + duration;
      existing.duration = duration;
      existing.sourceId = sourceId;
      existing.data = data;
      // Emit stack update for VFX
      eventBus.emit('vfx:status_stack_update', {
        targetId,
        effectId,
        stacks: existing.stacks,
        maxStacks,
        duration,
        sourceId,
        data,
      });
    } else {
      // New effect
      const effect: StatusEffect = {
        effectId, stacks: 1, maxStacks, duration,
        expiresAt: Date.now() + duration,
        tickDamage, tickRateMs, sourceId, data,
      };
      targetEffects.set(effectId, effect);

      // Emit for VFX. A server ability status names its ability
      // (data.abilityId, plan 29 F1) so the handler can fall back to that
      // ability's registered aura when the effect id itself has no visual.
      const abilityId = typeof data?.abilityId === 'string' ? data.abilityId : undefined;
      eventBus.emit('vfx:status_effect', {
        targetId, effectId, active: true, duration, sourceId, data, ...(abilityId ? { abilityId } : {}),
      });
    }

    this.scheduleExpiryCheck(targetId, effectId, duration);
  }

  /** Remove a status effect from a target */
  remove(targetId: string, effectId: string): void {
    const targetEffects = this.effects.get(targetId);
    if (!targetEffects) return;

    targetEffects.delete(effectId);

    eventBus.emit('vfx:status_effect', {
      targetId, effectId, active: false,
    });
  }

  /** Get current stacks for an effect on a target */
  getStacks(targetId: string, effectId: string): number {
    return this.effects.get(targetId)?.get(effectId)?.stacks ?? 0;
  }

  /** Check if a target has a specific effect active */
  hasEffect(targetId: string, effectId: string): boolean {
    const effect = this.effects.get(targetId)?.get(effectId);
    if (!effect) return false;
    return Date.now() < effect.expiresAt;
  }

  /** Get all active effects on a target */
  getEffects(targetId: string): StatusEffect[] {
    const targetEffects = this.effects.get(targetId);
    if (!targetEffects) return [];
    return Array.from(targetEffects.values()).filter(e => Date.now() < e.expiresAt);
  }

  /** Clean up all effects (e.g., on scene change) */
  clear(): void {
    this.effects.clear();
  }

  private scheduleExpiryCheck(targetId: string, effectId: string, delayMs: number): void {
    setTimeout(() => {
      const effect = this.effects.get(targetId)?.get(effectId);
      if (!effect) return;
      const remaining = effect.expiresAt - Date.now();
      if (remaining > 0) {
        // The effect was refreshed after this timeout was created. Re-arm for
        // the new boundary instead of letting the stale timer remove it early.
        this.scheduleExpiryCheck(targetId, effectId, remaining + 100);
        return;
      }
      this.remove(targetId, effectId);
    }, Math.max(0, delayMs) + 100);
  }
}

export const statusEffectManager = new StatusEffectManagerImpl();
